/**
 * DEPLOY-01 (Betreiberfreigabe 14.10., R3) – Ops-Center: Anzeige „Systemstart" (nur lesend).
 *
 * Quelle: GET /api/ops/startup-status (Prozessspeicher des Backends). Zeigt
 * „Start vollständig." bzw. „Start unvollständig: {Schritte}", den Startzeitpunkt
 * (Europe/Berlin, TT.MM.JJJJ hh:mm), das Ergebnis je Schritt (Fehlerklasse) und die
 * übersprungenen Altdatensätze je Startup-Migration (Anzahl). Keine Schreibaktion.
 * DEPLOY-02 (Betreiberfreigabe 14.10.): Liste „Hinweise" unter den Schritten (nur wenn vorhanden).
 */
import { useCallback, useEffect, useState } from "react";
import { Loader2, Power } from "lucide-react";
import { opsApi, opsErrorMessage } from "@/lib/opsApi";
import { Button } from "@/components/ui/button";

export const TEXT_VOLLSTAENDIG = "Start vollständig.";
export const textUnvollstaendig = (schritte) => `Start unvollständig: ${schritte.join(", ")}`;

export function fmtBerlin(iso) {
  if (!iso) return "–";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "–";
  const parts = new Intl.DateTimeFormat("de-DE", {
    timeZone: "Europe/Berlin",
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  }).formatToParts(d);
  const g = (t) => (parts.find((p) => p.type === t) || {}).value || "";
  return `${g("day")}.${g("month")}.${g("year")} ${g("hour")}:${g("minute")}`;
}

export function startText(data) {
  const fehlgeschlagen = Array.isArray(data?.fehlgeschlagen)
    ? data.fehlgeschlagen
    : (data?.schritte || []).filter((s) => s.status !== "ok").map((s) => s.name);
  return fehlgeschlagen.length === 0 ? TEXT_VOLLSTAENDIG : textUnvollstaendig(fehlgeschlagen);
}

export default function OpsSystemstartSection() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const load = useCallback(() => {
    if (typeof opsApi.startupStatus !== "function") return undefined;
    let cancelled = false;
    setLoading(true);
    setError("");
    opsApi
      .startupStatus()
      .then((d) => { if (!cancelled) setData(d); })
      .catch((e) => { if (!cancelled) setError(opsErrorMessage(e)); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  useEffect(() => load(), [load]);

  const vollstaendig = !!data && (data.fehlgeschlagen || []).length === 0;
  const schritte = data?.schritte || [];
  const uebersprungen = data?.uebersprungen || [];
  // DEPLOY-02, Betreiberfreigabe 14.10. (I2): Datenbank-Hinweise ändern den Starttext nicht.
  const hinweise = data?.hinweise || [];

  return (
    <div className="rounded-lg border border-slate-200 bg-white" data-testid="ops-systemstart">
      <div className="flex items-center gap-2 px-4 py-2.5 border-b border-slate-100 bg-slate-50/60">
        <Power className="w-4 h-4 text-slate-500" />
        <h3 className="font-heading text-sm font-semibold text-slate-800">Systemstart</h3>
        <div className="flex-1" />
        <Button variant="outline" size="sm" onClick={load} disabled={loading} data-testid="ops-systemstart-reload">
          Aktualisieren
        </Button>
      </div>
      <div className="p-4 space-y-3">
        {loading && (
          <div className="flex items-center gap-2 text-sm text-slate-500" data-testid="ops-systemstart-loading">
            <Loader2 className="w-4 h-4 animate-spin" /> Lade Startstatus…
          </div>
        )}
        {!loading && error && (
          <p className="text-sm text-red-600" data-testid="ops-systemstart-error">{error}</p>
        )}
        {!loading && !error && data && (
          <>
            <p
              className={`text-sm font-semibold ${vollstaendig ? "text-emerald-700" : "text-red-700"}`}
              data-testid="ops-systemstart-text"
            >
              {startText(data)}
            </p>
            <p className="text-xs text-slate-600" data-testid="ops-systemstart-zeitpunkt">
              Start: {fmtBerlin(data.gestartet_am)}
            </p>
            <ul className="grid grid-cols-1 sm:grid-cols-2 gap-1" data-testid="ops-systemstart-schritte">
              {schritte.map((s) => (
                <li key={s.name} className="flex items-center gap-2 text-xs" data-testid={`ops-systemstart-schritt-${s.name}`}>
                  <span className={`inline-block w-2 h-2 rounded-full ${s.status === "ok" ? "bg-emerald-500" : "bg-red-500"}`} />
                  <span className="text-slate-700">{s.name}</span>{" "}
                  <span className={s.status === "ok" ? "text-emerald-600" : "text-red-600"}>
                    {s.status === "ok" ? "ok" : `fehlgeschlagen${s.fehlerklasse ? ` (${s.fehlerklasse})` : ""}`}
                  </span>
                </li>
              ))}
            </ul>
            {hinweise.length > 0 && (
              <div data-testid="ops-systemstart-hinweise">
                <p className="text-xs font-semibold text-slate-600">Hinweise</p>
                <ul className="mt-1 space-y-0.5">
                  {hinweise.map((h) => (
                    <li
                      key={`${h.collection}/${h.index}`}
                      className="text-xs text-amber-700"
                      data-testid={`ops-systemstart-hinweis-${h.collection}-${h.index}`}
                    >
                      {h.text}
                    </li>
                  ))}
                </ul>
              </div>
            )}
            <div data-testid="ops-systemstart-uebersprungen">
              <p className="text-xs font-semibold text-slate-600">Übersprungene Altdatensätze je Migration</p>
              {uebersprungen.length === 0 ? (
                <p className="text-xs text-slate-500" data-testid="ops-systemstart-uebersprungen-leer">Keine Angaben.</p>
              ) : (
                <ul className="mt-1 space-y-0.5">
                  {uebersprungen.map((u) => (
                    <li
                      key={`${u.migration}/${u.collection}`}
                      className="text-xs text-slate-700"
                      data-testid={`ops-systemstart-uebersprungen-${u.migration}-${u.collection}`}
                    >
                      {u.migration} ({u.collection}): <span className={u.anzahl > 0 ? "font-semibold text-amber-700" : ""}>{u.anzahl}</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
